import { useState } from "react";
import { botTransferReasonKey } from "../../shared/bot-transfer";
import { formatDate } from "./core";
import { isOfflineMutation } from "./model";
import { parseValue, type StaffPaneProps } from "./console-types";
import "./bot-transfer-card.css";

/**
 * 🔴 `t` THROWS on a missing key (F106). A refusal is therefore mapped through
 * an ALLOWLIST, never by probing whether `t()` handed its own key back: an
 * unnamed code would throw inside the click handler before `setBusy(false)`
 * and leave the button disabled forever with nothing said.
 */
const RETURN_ERROR_CODES = [
  "no_failed_transfer", "transfer_completed", "state_conflict", "reason_required",
  // D11: the server refuses a SECOND return of the same job; the button is
  // gone by then, but a stale tab can still reach the route.
  "already_returned",
  "idempotency_mismatch", "invalid_request", "not_found",
  "forbidden", "csrf_invalid", "origin_invalid", "impersonation_readonly"
] as const;

const JOB_STATES = ["pending", "running", "done", "failed"] as const;

/**
 * F112 (Pavel 2026-08-10): "Ten partak s tim nemuze nic udelat, protoze kdyz
 * se na to koukne, vrati ho to na stranku s beta testovanim. Kdyz se na tu
 * stranku podiva, nema tam zadnou moznost vratit ten krok zpatky."
 *
 * The notification pointed the partner at a step that showed the transfer as
 * one word ("Zatim u nas") and offered nothing. This card is the step's honest
 * answer: what the machine tried, with WHICH two names, how many attempts, how
 * it ended, BotFather's own sentence, the dialog itself - and the one move that
 * unsticks it.
 *
 * The two names are printed side by side on purpose. The live failure was the
 * client pasting the bot's own username into the ownership form, and no
 * surface anywhere made that visible: both fields simply said "Pavelha_bot".
 */

function sameAccount(left: string, right: string): boolean {
  const clean = (value: string) => value.trim().replace(/^@/, "").toLowerCase();
  return clean(left).length > 0 && clean(left) === clean(right);
}

export function BotTransferCard(props: StaffPaneProps) {
  const job = props.detail.botTransfer ?? null;
  const transferRow = props.detail.resources.find((row) => String(row.key) === "bot_transfer_state");
  const transferState = transferRow ? String(parseValue(transferRow).state ?? "") : "";
  const transferred = transferState === "transferred";
  const [armed, setArmed] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!job) return null;

  // The action exists while a failed job is the last thing that happened and
  // the bot has NOT moved. A client who has already resubmitted has a fresh
  // pending job, so the button disappears by itself.
  //
  // D11 (audit F115): the job KEEPS its `failed` status after a return, so the
  // button used to stay live beside the quiet "Vráceno klientovi." note and a
  // second click sent the client a second mail about the same thing. Once it
  // is returned the ball is the client's; the next move is their resubmit,
  // which opens a fresh job and brings the action back on its own.
  const canReturn = job.status === "failed" && !transferred && !job.returnedAt;
  const recipientIsBot = sameAccount(job.botUsername, job.recipientUsername);

  async function returnToClient() {
    const trimmed = reason.trim();
    if (trimmed.length < 3) {
      setError(props.t("staffConsole.botTransferCard.reasonRequired"));
      return;
    }
    setBusy(true);
    setError(null);
    const response = await props.run(
      `/api/staff/clients/${props.detail.client.id}/bot-transfer/return`,
      { method: "POST", body: { reason: trimmed } }
    );
    if (response.ok) {
      setArmed(false);
      setReason("");
      setDone(true);
      await props.refresh();
    } else if (isOfflineMutation(response)) {
      setError(props.t("staffConsole.mutationOffline"));
    } else {
      let code = "";
      try {
        const body = await response.json() as { error?: string };
        if (typeof body.error === "string") code = body.error;
      } catch { /* the generic sentence below already covers an unreadable body */ }
      const named = (RETURN_ERROR_CODES as readonly string[]).includes(code);
      setError(props.t(named
        ? `staffConsole.botTransferCard.error.${code}`
        : "staffConsole.botTransferCard.error.generic"));
    }
    setBusy(false);
  }

  return (
    <article className="staff-card bot-transfer-card" data-bot-transfer-status={job.status}>
      <h3>{props.t("staffConsole.botTransferCard.title")}</h3>
      <p className="staff-card-hint">
        {props.t(`staffConsole.botTransferCard.state.${
          (JOB_STATES as readonly string[]).includes(job.status) ? job.status : "pending"}`)}
      </p>
      <dl className="staff-definition">
        <div>
          <dt>{props.t("staffConsole.botTransferCard.bot")}</dt>
          <dd>@{job.botUsername}</dd>
        </div>
        <div>
          <dt>{props.t("staffConsole.botTransferCard.recipient")}</dt>
          <dd>
            @{job.recipientUsername}
            {recipientIsBot && (
              <span className="bot-transfer-same-name">
                {props.t("staffConsole.botTransferCard.recipientIsBot")}
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt>{props.t("staffConsole.botTransferCard.queued")}</dt>
          <dd>{formatDate(job.createdAt, props.locale)}</dd>
        </div>
        <div>
          <dt>{props.t("staffConsole.botTransferCard.attempts")}</dt>
          <dd>{String(job.attempts)}</dd>
        </div>
        {job.completedAt && (
          <div>
            <dt>{props.t("staffConsole.botTransferCard.finished")}</dt>
            <dd>{formatDate(job.completedAt, props.locale)}</dd>
          </div>
        )}
        {job.reason && (
          <div>
            <dt>{props.t("staffConsole.botTransferCard.reason")}</dt>
            <dd>
              {props.t(botTransferReasonKey(job.reason))}
              <span className="bot-transfer-fault">
                {props.t(job.clientFault
                  ? "staffConsole.botTransferCard.faultClient"
                  : "staffConsole.botTransferCard.faultTeam")}
              </span>
            </dd>
          </div>
        )}
      </dl>
      {/* The verbatim sentence, never a paraphrase: it is the only thing that
          names a refusal our classifier does not know yet. */}
      {job.providerMessage && (
        <p className="bot-transfer-quote">
          <span>{props.t("staffConsole.botTransferCard.botfather")}</span>
          <q>{job.providerMessage}</q>
        </p>
      )}
      {job.transcript && (
        <details className="bot-transfer-transcript">
          <summary>{props.t("staffConsole.botTransferCard.transcript")}</summary>
          <pre>{job.transcript}</pre>
        </details>
      )}
      {job.returnedAt && (
        <>
          <p className="bot-transfer-returned" role="status">
            {props.t("staffConsole.botTransferCard.returned")
              .replace("{date}", formatDate(job.returnedAt, props.locale))
              .replace("{reason}", job.returnReason ?? "")}
          </p>
          {/* A closed control says why, next to itself (S2-19). */}
          <p className="staff-card-hint bot-transfer-return-closed">
            {props.t("staffConsole.botTransferCard.returnedClosed")}
          </p>
        </>
      )}
      {canReturn && (
        <div className="bot-transfer-return">
          <p className="staff-card-hint">{props.t("staffConsole.botTransferCard.returnHint")}</p>
          {armed ? (
            <div className="bot-transfer-confirm" role="alert">
              <p>
                {props.t("staffConsole.botTransferCard.returnConfirm")
                  .replace("{client}", props.detail.client.name)}
              </p>
              <label className="bot-transfer-reason">
                <span>{props.t("staffConsole.botTransferCard.reasonLabel")}</span>
                <textarea
                  rows={2}
                  value={reason}
                  placeholder={props.t("staffConsole.botTransferCard.reasonPlaceholder")}
                  onChange={(event) => setReason(event.target.value)}
                />
              </label>
              {error && <p className="message error-message" role="alert">{error}</p>}
              <div className="button-row">
                <button
                  className="primary-button compact-button"
                  type="button"
                  disabled={busy}
                  onClick={() => void returnToClient()}
                >
                  {props.t(busy
                    ? "staffConsole.botTransferCard.returning"
                    : "staffConsole.botTransferCard.confirmReturn")}
                </button>
                <button
                  className="quiet-link"
                  type="button"
                  disabled={busy}
                  onClick={() => { setArmed(false); setError(null); }}
                >
                  {props.t("common.cancel")}
                </button>
              </div>
            </div>
          ) : (
            <div className="button-row">
              <button
                className="primary-button compact-button"
                type="button"
                onClick={() => { setArmed(true); setError(null); }}
              >
                {props.t("staffConsole.botTransferCard.returnButton")}
              </button>
              {done && (
                <span className="message success-message">
                  {props.t("staffConsole.botTransferCard.returnDone")}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  );
}
